import { v5 as uuidv5 } from "uuid";
import _ from 'lodash';

const API_URL = (window.location.host.includes('localhost'))
  ? 'http://localhost:3000'
  : (window.location.host !== 'www.brookings.edu')
    ? 'https://usmca-api-staging.brookings.edu'
    : 'https://usmca-api.brookings.edu';

const WP_API_URL = (window.location.host === 'www.brookings.edu')
  ? 'https://www.brookings.edu'
  : 'https://bi-web-develop.teal.media';

const goodsStore = {
  /**
   * Shared State of the Goods Trade Flow Over Time component.
   *
   * This data is needed across multiple components. Should be
   * considered read-only outside of the Goods Trade Flow Over Time
   * component.
   */
  state: {
    state: {
      name: 'Alabama',
      value: 33,
      label: 'Alabama',
    },
    hsLevel: { "name": "HS1", "value": "HS1" },
    selectedGood: {
      hsid: 1,
      id: 'hs1id-1',
      name: "1. Live animals and animal products",
      value: 1,
    },
    additionalGoods: [],
    primaryCountry: 'usa',
    partnerCountry: 'mex',
    direction: 'exportsTo',
    year: 2022,
    loading: false,
    initialized: false,
    apiCalls: 0,
  },

  async queryJobs(filters) {
    return await this.query('jobs', {
      country: this.state.primaryCountry,
      partner: this.state.partnerCountry,
      year: this.state.year,
      ...filters,
    });
  },

  async getAllGoodsJobsByCountry() {
    return await this.queryJobs();
  },

  async getAllGoodsJobsByState(stateid) {
    return await this.queryJobs({
      stateid: stateid ?? this.state.state.value,
    });
  },

  async getSelectedGoodJobsByCountry(hsLevel, good) {
    if(!hsLevel) {
      return 0;
    }

    return await this.queryJobs({
      [`hs${hsLevel}id`]: good?.hsid,
    })
  },

  async getSelectedGoodJobsByState(stateid, hsLevel, good) {
    if(!hsLevel) {
      return 0;
    }

    return await this.queryJobs({
      stateid: stateid ?? this.state.state.value,
      [`hs${hsLevel}id`]: good?.hsid,
    })
  },

  async getTopThreeGoods() {
    return await this.query(
      'top-goods',
      {
        country: this.state.primaryCountry,
        partner: this.state.partnerCountry,
        year: this.state.year,
        direction: (this.state.direction === 'exportsTo') ? 'exports' : 'imports',
        level: this.state.hsLevel.value.slice(-1),
        stateid: this.state.state.value,
      }
    );
  },

  /**
   * Perform a generic query to the API using the filters.
   *
   * @param {string} endpoint The endpoint name to query.
   * @param {object} filters An object of key value pairs to use as query parameters.
   * @return {mixed} The API response.
   */
  async query(endpoint, filters) {
    const query = this.buildQueryFromFilters(filters);

    const url = `${API_URL}/v1/usmca/${endpoint}${query}`;

    const cachedResponse = this.getFromCache(url);

    if( typeof cachedResponse !== 'undefined' ){
      return cachedResponse;
    }

    const response = await fetch(url);
    const data = await response.json();

    this.addToCache(url, data);

    return data;
  },

  /**
   * Perform a query to the Exports API endpoint.
   *
   * This method is used to remove duplication of required fields.
   *
   * @param {object} filters Additional filters to add to the required fields.
   * @return {mixed} The export value.
   */
  async queryExports(filters) {
    const exports = await this.query(
      'goods/exports',
      {
        country: this.state.primaryCountry,
        partner: this.state.partnerCountry,
        year: this.state.year,
        ...filters
      }
    );

    return exports?.exports ?? 0;
  },

  /**
   * Get the total export amount from the primary country to the partner country for all goods
   * in a year. For example, all goods exported from Canada to Mexico in 2021.
   *
   * @return {number} The export value.
   */
  async getTotalExportsByCountry() {
    return await this.queryExports();
  },

  /**
   * Gets the total export amount of all goods from the state in the primary country to the partner country
   * in a year. For example, all goods exported from Alberta, Canada to Mexico in 2021.
   *
   * @return {number} The export value.
   */
  async getTotalExportsByState(stateid) {
    return await this.queryExports({ stateid: stateid ?? this.state.state.value });
  },

  /**
   * Gets the total export amount of a specific good from the entire primary country to the partner country
   * in a year. For example, Live animals and animal products exported from Canada to Mexico in 2021.
   *
   * @param {number} hsLevel The HS Level (1,2, or 4) of the good.
   * @param {object} good The good to get the export value from.
   * @return {number} The export value.
   */
  async getGoodExportsByCountry(hsLevel, good) {
    if(!hsLevel) {
      return 0;
    }

    if(!good?.hsid) {
      return 0;
    }

    return await this.queryExports({
      [`hs${hsLevel}id`]: good?.hsid,
    });
  },

  /**
   * Gets the total export amount of a specific good from the entire primary country to the partner country
   * in a year. For example, Live animals and animal products exported from Alberta, Canada to Mexico in 2021.
   *
   * @param {number} hsLevel The HS Level (1,2, or 4) of the good.
   * @param {object} good The good to get the export value from.
   * @return {number} The export value.
   */
  async getGoodExportsByState(hsLevel, good) {
    if(!hsLevel) {
      return 0;
    }

    if(!good?.hsid) {
      return 0;
    }

    return await this.queryExports({
      stateid: this.state.state.value,
      [`hs${hsLevel}id`]: good?.hsid,
    });
  },

  /**
   * Perform a query to the Imports API endpoint.
   *
   * This method is used to remove duplication of required fields.
   *
   * @param {object} filters Additional filters to add to the required fields.
   * @return {mixed} The export value.
   */
  async queryImports(filters) {
    const imports = await this.query(
      'goods/imports',
      {
        country: this.state.primaryCountry,
        partner: this.state.partnerCountry,
        year: this.state.year,
        ...filters
      }
    );

    return imports?.imports ?? 0;
  },

  /**
   * Get the total import amount to the primary country from the partner country for all goods
   * in a year. For example, all goods imported to Canada from Mexico in 2021.
   *
   * @return {number} The import value.
   */
  async getTotalImportsByCountry() {
    return await this.queryImports();
  },

  /**
   * Gets the total import amount of all goods to the state in the primary country from the partner country
   * in a year. For example, all goods imported to Alberta, Canada from Mexico in 2021.
   *
   * @return {number} The import value.
   */
  async getTotalImportsByState(stateid) {
    return await this.queryImports({ stateid: stateid ?? this.state.state.value });
  },

  /**
   * Gets the total import amount of a specific good to the entire primary country from the partner country
   * in a year. For example, Live animals and animal products imported to Canada from Mexico in 2021.
   *
   * @param {number} hsLevel The HS Level (1,2, or 4) of the good.
   * @param {object} good The good to get the import value of.
   * @return {number} The import value.
   */
  async getGoodImportsByCountry(hsLevel, good) {
    if(!hsLevel) {
      return 0;
    }

    return await this.queryImports({
      [`hs${hsLevel}id`]: good?.hsid,
    });
  },

  /**
   * Gets the total import amount of a specific good to a specific state in the primary country from the partner country
   * in a year. For example, Live animals and animal products imported to Alberta, Canada from Mexico in 2021.
   *
   * @param {number} hsLevel The HS Level (1,2, or 4) of the good.
   * @param {object} good The good to get the import value of.
   * @return {number} The import value.
   */
  async getGoodImportsByState(hsLevel, good) {
    if(!hsLevel) {
      return 0;
    }

    return await this.queryImports({
      stateid: this.state.state.value,
      [`hs${hsLevel}id`]: good?.hsid,
    });
  },

  /**
   * Gets the chart data array for the country ShareOfTotal chart.
   *
   * @param {number[]} years The array of years to get data for.
   * @param {object[]} selectedGoods The array of good objects that have been selected.
   * @param {number} hsLevel The HS Level to look for goods in.
   * @return {object[]} The array of chart data objects.
   */
  async getShareOfTotalExportsByCountryOverTime(years, selectedGoods, hsLevel) {
    let data = [];

    if ( selectedGoods.filter((value) => value).length > 0 ){
      for( let year of years ) {
        const yearTotalExports = await this.queryExports({ year: year });

        for( let selectedGood of selectedGoods ) {
          if ( yearTotalExports === 0 ){
            data.push({
              year: year,
              hsid: selectedGood.hsid,
              percentage: 0,
            });
            continue;
          }

          const selectedGoodExports = await this.queryExports({
            year: year,
            [`hs${hsLevel}id`]: selectedGood.hsid
          });

          data.push({
            year: year,
            hsid: selectedGood.hsid,
            percentage: (selectedGoodExports > 0) ? Math.round((selectedGoodExports / yearTotalExports * 100) * 100) / 100 : 0,
          });
        }
      }
    }

    return data;
  },

  /**
   * Gets the chart data array for the state ShareOfTotal chart.
   *
   * @param {number[]} years The array of years to get data for.
   * @param {object[]} selectedGoods The array of good objects that have been selected.
   * @param {number} hsLevel The HS Level to look for goods in.
   * @return {object[]} The array of chart data objects.
   */
  async getShareOfTotalExportsByStateOverTime(years, selectedGoods, hsLevel) {
    let data = [];

    if ( selectedGoods.filter((value) => value).length > 0 ){
      for( let year of years ) {
        const yearTotalExports = await this.queryExports({
          year: year,
          stateid: this.state.state.value,
        });

        for( let selectedGood of selectedGoods ) {
          if ( yearTotalExports === 0 ){
            data.push({
              year: year,
              hsid: selectedGood.hsid,
              percentage: 0,
            });
            continue;
          }

          const selectedGoodExports = await this.queryExports({
            year: year,
            stateid: this.state.state.value,
            [`hs${hsLevel}id`]: selectedGood.hsid
          });

          data.push({
            year: year,
            hsid: selectedGood.hsid,
            percentage: (selectedGoodExports > 0) ? Math.round((selectedGoodExports / yearTotalExports * 100) * 100) / 100 : 0,
          });
        }
      }
    }

    return data;
  },

  /**
   * Gets the chart data array for the country TotalValue chart.
   *
   * @param {number[]} years The array of years to get data for.
   * @param {object[]} selectedGoods The array of good objects that have been selected.
   * @param {number} hsLevel The HS Level to look for goods in.
   * @return {object[]} The array of chart data objects.
   */
  async getExportsTotalValueByCountryOverTime(years, selectedGoods, hsLevel) {
    let data = [];

    if ( selectedGoods.filter((value) => value).length > 0 ){
      for( let year of years ) {
        for( let selectedGood of selectedGoods ) {
          const selectedGoodExports = await this.queryExports({
            year: year,
            [`hs${hsLevel}id`]: selectedGood.hsid
          });

          data.push({
            year: year,
            hsid: selectedGood.hsid,
            export: selectedGoodExports,
          });
        }
      }
    }

    return data;
  },

  /**
   * Gets the chart data array for the state TotalValue chart.
   *
   * @param {number[]} years The array of years to get data for.
   * @param {object[]} selectedGoods The array of good objects that have been selected.
   * @param {number} hsLevel The HS Level to look for goods in.
   * @return {object[]} The array of chart data objects.
   */
  async getExportsTotalValueByStateOverTime(years, selectedGoods, hsLevel) {
    let data = [];

    if ( selectedGoods.filter((value) => value).length > 0 ){
      for( let year of years ) {
        for( let selectedGood of selectedGoods ) {
          const selectedGoodExports = await this.queryExports({
            year: year,
            stateid: this.state.state.value,
            [`hs${hsLevel}id`]: selectedGood.hsid
          });

          data.push({
            year: year,
            hsid: selectedGood.hsid,
            export: selectedGoodExports,
          });
        }
      }
    }

    return data;
  },

  async queryDataTable(filters) {
    return await this.query('goods/data-table', {
      country: this.state.primaryCountry,
      partner: this.state.partnerCountry,
      year: this.state.year,
      ...filters
    });
  },

  async getAllGoodsTableData() {
    return await this.queryDataTable({ direction: (this.state.direction === 'exportsTo') ? 'exports' : 'imports' });
  },

  async getAllGoodsJobsTableData() {
    return await this.queryDataTable({
      jobs: true,
    })
  },

  async getSelectedGoodJobsTableData() {
    const hsLevel = this.state?.hsLevel?.value?.slice(-1);

    if(!this.state.selectedGood?.hsid) {
      return {};
    }

    return await this.queryDataTable({
      jobs: true,
      [`hs${hsLevel}id`]: this.state.selectedGood?.hsid,
    })
  },

  async getSelectedGoodPercentageTableData() {
    const hsLevel = this.state?.hsLevel?.value?.slice(-1);

    if(!this.state.selectedGood?.hsid) {
      return {};
    }

    const data = await this.queryDataTable({
      direction: (this.state.direction === 'exportsTo') ? 'exports' : 'imports',
      percentage: true,
      [`hs${hsLevel}id`]: this.state.selectedGood?.hsid,
    });

    return data;
  },

  async getSelectedGoodValueTableData() {
    const hsLevel = this.state?.hsLevel?.value?.slice(-1);

    if(!this.state.selectedGood?.hsid) {
      return {};
    }

    const data = await this.queryDataTable({
      direction: (this.state.direction === 'exportsTo') ? 'exports' : 'imports',
      [`hs${hsLevel}id`]: this.state.selectedGood?.hsid,
    });

    return data;
  },

  async queryChart(startYear, endYear, selectedGoods, hsLevel, additionalFilters = {}) {
    const hsIds = selectedGoods.map((good) => good?.hsid).filter((good) => good);

    if(hsIds.length <= 0) {
      return [];
    }

    let response = await this.query('goods/chart-data', {
      country: this.state.primaryCountry,
      partner: this.state.partnerCountry,
      startYear: startYear,
      endYear: endYear,
      direction: (this.state.direction === 'exportsTo') ? 'exports' : 'imports',
      [`hs${hsLevel}id`]: hsIds,
      ...additionalFilters,
    });

    return response.map((data) => {
      if(data.totalvalue === 0) {
        return data;
      }

      let newData = data;
      // Normalize big numbers.
      newData.totalvalue = newData.totalvalue / 1000000;
      return newData;
    });
  },

  async getSelectedGoodsCountryChartData(startYear, endYear, selectedGoods, hsLevel) {
    return await this.queryChart(startYear, endYear, selectedGoods, hsLevel);
  },

  async getSelectedGoodsStateChartData(startYear, endYear, selectedGoods, hsLevel, stateid) {
    return await this.queryChart(startYear, endYear, selectedGoods, hsLevel, {
      stateid: stateid,
    });
  },

  /**
   * Get PDF urls for the regional summaries.
   */
  async getPdfUrls() {
    const url = `${WP_API_URL}/wp-json/brookings/v1/usmca`;

    // Check for cached response.
    const cachedResponse = this.getFromCache(url);

    if( typeof cachedResponse !== 'undefined' ){
      return cachedResponse;
    }

    // If not in cache, fetch from server.
    try {
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();

      // Add successful response to cache.
      this.addToCache(url, data);

      return data;
    } catch (error) {
      console.error(`Error fetching ${url}:`, error);
      throw error;
    }
  },

  graphCache: {},

  /**
   * Storage location for the data returned from the most
   * recent call to `callDatabase`. This is the data that
   * should be actively used.
   *
   * @shape {
   *   country: string,
   *   countryexports: number,
   *   countryimports: number,
   *   partner: string,
   *   states: {
   *     [stateid: Number]: {
   *       exports: number,
   *       imports: number,
   *       totaldirectjobs: number,
   *       totalindirectjobs: number,
   *       hs: {
   *         1: {
   *           [hs1id: number]: { id: number, exports: number, imports: number, directjobs: number, indirectjobs: number }
   *         },
   *         2: {
   *           [hs2id: number]: { id: number, exports: number, imports: number, directjobs: number, indirectjobs: number }
   *         }
   *         4: {
   *           [hs2id: number]: { id: number, exports: number, imports: number, directjobs: number, indirectjobs: number }
   *         }
   *       }
   *     }
   *   },
   *   hs: {
   *     1: {
   *       [hs1id: number]: { id: number, exports: number, imports: number, directjobs: number, indirectjobs: number }
   *     },
   *     2: {
   *       [hs2id: number]: { id: number, exports: number, imports: number, directjobs: number, indirectjobs: number }
   *     }
   *     4: {
   *       [hs2id: number]: { id: number, exports: number, imports: number, directjobs: number, indirectjobs: number }
   *     }
   *   },
   *   totaldirectjobs: number,
   *   totalexports: number,
   *   totalimports: number,
   *   totalindirectjobs: number,
   *   year: number,
   * }
   */
  all: {
    data: {},
  },

  /**
   * Save expensive calls to the database by caching requests here.
   *
   * Cache should be based on Primary Country, State ID, Partner Country, and Year.
   * Nothing else is used to query the database, so any unique combination of these
   * will return the same dataset as another.
   */
  cached: {},

  updateSelectedGood(selectedGood) {
    this.state.selectedGood = selectedGood;
  },

  getSelectedGood() {
    return this.state.selectedGood;
  },

  getSelectedYear() {
    return this.state.year;
  },

  updateState(newState) {
    this.state = { ...this.state, ...newState };
  },

  processDataByYear(data, filters) {
    let years = _.groupBy(data, 'year');
    let keys = Object.keys(years);

    let yearsData = {};

    keys.forEach((year) => {
      // Generate a URL for this year, specifically.
      let url = this.buildUrlFromFilter({
        ...filters,
        year: year,
      });

      // Process the individual data.
      yearsData[year] = this.processData(years[year]);

      // Cache the individual year's data as if we had called that year URL directly.
      this.addToCache(url, yearsData[year]);
    });

    return yearsData;
  },

  processData(data) {
    if(data.length === 0){
      return {};
    }

    let states = {};

    data.forEach((dataPoint) => {
      let state = {
        exports: 0,
        imports: 0,
        hs: {
          1: {},
          2: {},
          4: {},
        },
      };

      let stateHS = {
        1: {},
        2: {},
        4: {},
      };

      /**
       * For HS data we need to aggregate based on the State ID. For instance, ALL hs1directjobs with a state ID of 33 and an HS1 ID of 1 will have the same value, which we don't want to duplicate.
       * But StateID of 34 with an HS1 ID of 1 will have a different value, but they will all match the others with this combination of State ID and HS ID.
       *
       * These values are a roll up of the HS4 data within the category. HS1 is an aggregate of HS2, which is an aggregate of HS4. Each HS level isn't _different_ so much as a smaller sub-section
       * of the previous HS level.
       */

      if( typeof states[dataPoint.stateid] !== 'undefined' ) {
        state = states[dataPoint.stateid];
        stateHS = state.hs;
      }

      if( dataPoint.hs1id ) {
        stateHS[1][dataPoint.hs1id] = {
          id: dataPoint.hs1id,
          exports: dataPoint.hs1exports,
          imports: dataPoint.hs1imports,
          exportsbystate: dataPoint.hs1exportsbystate,
          importsbystate: dataPoint.hs1importsbystate,
          directjobs: dataPoint.hs1directjobs,
          indirectjobs: dataPoint.hs1indirectjobs,
          directjobsbystate: dataPoint.hs1directjobsbystate,
          indirectjobsbystate: dataPoint.hs1indirectjobsbystate,
        };
      }

      if( dataPoint.hs2id ) {
        stateHS[2][dataPoint.hs2id] = {
          id: dataPoint.hs2id,
          exports: dataPoint.hs2exports,
          imports: dataPoint.hs2imports,
          exportsbystate: dataPoint.hs2exportsbystate,
          importsbystate: dataPoint.hs2importsbystate,
          directjobs: dataPoint.hs2directjobs,
          indirectjobs: dataPoint.hs2indirectjobs,
          directjobsbystate: dataPoint.hs2directjobsbystate,
          indirectjobsbystate: dataPoint.hs2indirectjobsbystate,
        };
      }

      if( dataPoint.hs4id ) {
        stateHS[4][dataPoint.hs4id] = {
          /**
           * HS4 items have unique IDs, but roll up into a common HS2 id.
           * These HS2 items then roll up into a common HS1 id. When gathering
           * jobs data at the HS1 and HS2 levels at a per-state level, we need
           * to be able to get a summation of all HS4 IDs within that HS1 or HS2
           * ID grouping. To do that, we need to use these fields.
           */
          id: dataPoint.hs4id,
          hs2id: dataPoint.hs2id,
          hs1id: dataPoint.hs1id,
          exports: dataPoint.hs4exports,
          imports: dataPoint.hs4imports,
          exportsbystate: dataPoint.hs4exportsbystate,
          importsbystate: dataPoint.hs4importsbystate,
          usmcadirectjobs: dataPoint.hs4directjobs,
          usmcaindirectjobs: dataPoint.hs4indirectjobs,
          directjobsbystate: dataPoint.hs4directjobsbystate,
          indirectjobsbystate: dataPoint.hs4indirectjobsbystate,
        };
      }

      states[dataPoint.stateid] = {
        id: dataPoint.stateid,
        exports: dataPoint.exportsbystate,
        imports: dataPoint.importsbystate,
        usmcadirectjobs: dataPoint.usmcadirectjobsbystate,
        usmcaindirectjobs: dataPoint.usmcaindirectjobsbystate,
        hs: stateHS,
      }
    });

    /**
     * Sometimes the data we import has no data in some of the columns for the country-wide data that
     * doesn't change, while in other columns it will have the correct data. For that reason we need to
     * look at the records and find the first record that has a value, and we'll use that value.
     */
    const totalimports = this.getFirstValueForField(data, 'totalimp');
    const totalexports = this.getFirstValueForField(data, 'totalexp');
    const countryimports = this.getFirstValueForField(data, 'countryimports');
    const countryexports = this.getFirstValueForField(data, 'countryexports');
    const totaldirectjobs = this.getFirstValueForField(data, 'totaldirectjobs');
    const totalindirectjobs = this.getFirstValueForField(data, 'totalindirectjobs');
    const usmcadirectjobs = this.getFirstValueForField(data, 'usmcadirectjobs');
    const usmcaindirectjobs = this.getFirstValueForField(data, 'usmcaindirectjobs');

    return {
      country: data[0].country,
      countryexports: countryexports,
      countryimports: countryimports,
      partner: data[0].partner,
      states: states,
      totalexports: totalexports,
      totalimports: totalimports,
      totaldirectjobs: totaldirectjobs,
      totalindirectjobs: totalindirectjobs,
      usmcadirectjobs: usmcadirectjobs,
      usmcaindirectjobs: usmcaindirectjobs,
      year: data[0].year,
    };
  },

  getFirstValueForField(dataSet, field) {
    const record = dataSet.find((record) => parseInt(record[field]) > 0);

    if(typeof record === 'undefined'){
      return 0;
    }

    return parseInt(record[field]);
  },

  hydrateAllGoodsData() {
    const filters = {
      country: this.state.primaryCountry?.toUpperCase() ?? '',
      partner: this.state.partnerCountry?.toUpperCase() ?? '',
      year:    [2021],
    };

    let url = this.buildUrlFromFilter({
      ...filters,
      year: this.state.year,
    });

    const cachedResponse = this.getFromCache(url);

    if( typeof cachedResponse !== 'undefined' ){
      console.log('[hydrateAllGoodsData] cachedResponse: ', cachedResponse );
      this.all.data = cachedResponse;
      this.state.isLoading = false;
      this.state.initialized = true;
      return;
    }

    this.queryAPI(filters, null, true)
      .then((data) => {
        if(data.length === 0) {
          return [];
        }

        let allYearsData = this.processDataByYear(data, filters);

        this.all.data = allYearsData[this.state.year];
      })
      .then(() => {
        this.state = {
          ...this.state,
          selectedGood: {
            "hsid": 1,
            "name": "1. Live animals and animal products",
            "id": "hs1id-1",
            "value": 1
          },
          isLoading: false,
          initialized: true,
        }
      });
  },

  buildUrlFromFilter(filters) {
    const query = this.buildQueryFromFilters(filters);

    // Putting it all together, now.
    return `${API_URL}/v1/usmca/goods${query}`;
  },

  buildQueryFromFilters(filters) {
    let url = '/?';

    for( let [key,value] of Object.entries(filters) ) {
      let separator = (url !== '/?') ? '&' : '';

      // If the value is an array, we encode all the elements, and flatten it to a single string.
      // Otherwise, we just encode the URI component.
      value = Array.isArray(value) ? `${value.map((curVal) => encodeURIComponent(curVal)).join(`&${key}=`)}` : encodeURIComponent(value);

      url = `${url}${separator}${key}=${value}`;
    }

    return url;
  },

  /**
   * Perform an API call, and fire a callback.
   *
   * @param Object filters An object of key/value pairs where the key is an API field, and the value is the value to be filtered on.
   * @param Function callback The callback to pass the response JSON to.
   */
  queryAPI(filters, callback, loadingScreen = false) {

    // Putting it all together, now.
    const url = this.buildUrlFromFilter(filters);

    return fetch(url)
      .then(async (response) => {
        if (loadingScreen !== true || this.state.loading === true) {
          return response.json();
        }

        this.state.loading = true;

        let overlay = document.querySelector('.full-overlay');
        let loadingBar = document.getElementById('loading-bar');
        let loadingText = document.getElementById('load__num');

        // Reset the loading bar and text to 0
        loadingBar.style.width = '0%';
        loadingText.innerText = '0';

        overlay.style.display = 'flex';
        overlay.style.opacity = '1';

        const reader = response.body.getReader();

        // Grab our custom headers from the API response.
        const maxSize = +response.headers.get('X-Estimated-Max-Size');
        const documentCount = +response.headers.get('X-Estimated-Document-Count');
        const totalDocumentCount = +response.headers.get('X-Total-Document-Count');

        /**
         * We can estimate the size of our response content by getting the percentage
         * of documents returned in the response and using that same percentage of
         * the maximum size as our upper limit. If we're wrong, we'll have it sit at
         * the higher end for a moment.
         */
        const documentPercentage = (documentCount / totalDocumentCount);

        // We're estimating, and sometimes our estimates are very low, so we bump the percentage up a bit.
        const contentLength = Math.ceil( maxSize * (documentPercentage * 1.75) );

        let receivedLength = 0; // received that many bytes at the moment
        let chunks = []; // array of received binary chunks (comprises the body)
        while(true) {
          const {done, value} = await reader.read();
          if (done) {
            break;
          }

          chunks.push(value);
          receivedLength += value.length;

          // We give content length an additional 10%, because we're going to set that later, manually.
          let percentage = Math.round((receivedLength / (contentLength + (contentLength * .1))) * 100);

          /**
           * Lets restrict the top end of this to 95%, so we don't end up with a bar that shows 116%, due to our estimations.
           *
           * The risk here is that we have a download that is substantially higher than our estimate, which would cause
           * this loading time to appear as if the download has frozen. If that becomes the case, we can use another indicator
           * like an ellipsis that animates adding one, two, then three periods, to indicate to the end user that the page isn't
           * frozen, it's just taking a while.
           *
           * Another option would be to add a timeout before this loop that fires after 30 seconds or so and adds a note to the
           * loading screen to indicate to the user that loading times can sometimes be long.
           */
          if(percentage > 95) {
            percentage = 95;
          }

          loadingBar.style.width = `${percentage}%`;
          loadingText.innerText = percentage;
        }

        loadingBar.style.width = '100%';
        loadingText.innerText = '100';

        setTimeout(() => overlay.style.opacity = 0, 300);
        setTimeout(() => overlay.style.display = "none", 600);

        /**
         * We can't re-use the Response object after processing the stream,
         * which is why we use Chunks. Here we need to recombine the chunks into
         * the actual response data.
         */
        let chunksAll = new Uint8Array(receivedLength);
        let position = 0;
        for(let chunk of chunks) {
          chunksAll.set(chunk, position);
          position += chunk.length;
        }

        /**
         * Now that we have our raw data chunks, we need to decode that
         * back into an interpretable string.
         */
        const result = new TextDecoder("utf-8").decode(chunksAll);

        /**
         * Okay! We have our result string. We know this will be a JSON string, so let's parse it!.
         */
        return JSON.parse(result);
      })
      .then((json) => json.data)
      .then((data) => {
        if (typeof callback === 'function') {
          callback(data);
        }

        this.state.loading = false;
        this.state.apiCalls++;

        return data;
      })

  },

  // Generate a UUID from a key.
  cacheKey(key) {
    return uuidv5( key, 'bbfb36b8-3133-46b6-9a1d-b26ca355cb45');
  },

  addToCache(key, data) {
    const id = this.cacheKey(key);

    this.cached[id] = data;

    try {
      sessionStorage.setItem(id, JSON.stringify(data));
    }catch(e){
      // Ignore errors here. This is a nice to have, but not a necessity.
    }
  },

  getFromCache(key) {
    const id = this.cacheKey(key);

    if(typeof this.cached[id] !== 'undefined') {
      return this.cached[id];
    }

    if(!this.storageAvailable('sessionStorage')){
      return undefined;
    }

    let value = sessionStorage.getItem(id);

    if(value === null){
      return undefined;
    }

    return JSON.parse(value);
  },

  callDatabase(){
    //this.hydrateAllGoodsData();
  },

  storageAvailable(type) {
    let storage;
    try {
        storage = window[type];
        const x = '__storage_test__';
        storage.setItem(x, x);
        storage.removeItem(x);
        return true;
    }
    catch (e) {
        return e instanceof DOMException && (
            // everything except Firefox
            e.code === 22 ||
            // Firefox
            e.code === 1014 ||
            // test name field too, because code might not be present
            // everything except Firefox
            e.name === 'QuotaExceededError' ||
            // Firefox
            e.name === 'NS_ERROR_DOM_QUOTA_REACHED') &&
            // acknowledge QuotaExceededError only if there's something already stored
            (storage && storage.length !== 0);
    }
  }

}
export {goodsStore};
